import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Signal,
  ViewChild,
  WritableSignal,
  computed,
  input,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ModalComponent, TagComponent } from '@catch/general-ui';
import { ScreenService, ToastsService } from '@catch/services';
import { TranslocoModule } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TableModule } from 'primeng/table';
import { ButtonComponent } from '../../../../components/button/button.component';
import { ContractSelectorComponent } from '../../../../components/contract-selector/contract-selector.component';
import { JobPlatformsBrowserComponent } from '../../../../components/job-platforms-browser/job-platforms-browser.component';
import { PlatformsPricingListComponent } from '../../../../components/platforms-pricing-list/platforms-pricing-list.component';
import { SourcingCampaignBookingModalComponent } from '../../../../components/sourcing-campaign-booking-modal/sourcing-campaign-booking-modal.component';
import { ViewLoaderComponent } from '../../../../components/view-loader/view-loader.component';
import {
  CREDIT_COST_SOURCING_AUTOPILOT,
  CREDIT_COST_SOURCING_LEADS,
} from '../../../../constants/credit-cost.constant';
import {
  DEFAULT_SOCIAL_BOOSTER_AGENT_ADDON_PRICE,
  DEFAULT_SOCIAL_BOOSTER_PRICE,
} from '../../../../constants/social-booster-price.constant';
import { CatchJobPlatform } from '../../../../enums/catch-job-platforms.enum';
import { MultipostingProvider } from '../../../../enums/multiposting-provider.enum';
import { SlotType } from '../../../../enums/slot-type.enum';
import { SourcedJobsCampaignMode } from '../../../../enums/sourced-jobs-campaign-mode.enum';
import {
  JobPlatform,
  PlatformIdAndProvider,
} from '../../../../interfaces/job-platform';
import { CompanyPOSubscription } from '../../../../interfaces/po-subscriptions';
import { FormatPricePipe } from '../../../../pipes/format-price.pipe';
import { FontawesomeService } from '../../../../services/fontawesome.service';
import { JobPlatformsService } from '../../../../services/job-platforms.service';

@UntilDestroy()
@Component({
  selector: 'app-campaign-products-publish',
  templateUrl: './campaign-products-publish.component.html',
  styleUrls: ['./campaign-products-publish.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    NgSelectModule,
    FontAwesomeModule,
    ModalComponent,
    TagComponent,
    FormatPricePipe,
    DecimalPipe,
    ContractSelectorComponent,
    JobPlatformsBrowserComponent,
    PlatformsPricingListComponent,
    ViewLoaderComponent,
    TableModule,
    ButtonComponent,
    SourcingCampaignBookingModalComponent,
  ],
})
export class CampaignProductsPublishComponent implements OnInit {
  @ViewChild(ModalComponent) jobplatformModal: ModalComponent;
  @ViewChild('contractSelector') contractSelector: ContractSelectorComponent;
  @ViewChild('sourcingCampaignBookingModal')
  sourcingCampaignBookingModal: SourcingCampaignBookingModalComponent;
  @Input() submitAttempt: boolean;
  @Output() initialized: EventEmitter<boolean> = new EventEmitter();
  slotType: Signal<SlotType> = input(null);
  bookedPlatformIds = input<string[]>([]);
  @ViewChild('jobPlatformsBrowser')
  jobPlatformsBrowser: JobPlatformsBrowserComponent;

  SlotType = SlotType;
  // JobPlatforms
  vat: number = 0.19;

  isLoading = false;
  jobPlatformFilter = new FormControl();
  jobPostingLink = new FormControl(null, Validators.pattern('^[A-Za-z0-9-]+$'));
  subscription = new FormControl(null);
  alreadyBooked = false;
  isOpen = false;

  poSubscriptions: CompanyPOSubscription[];
  jobCrossPostings: string[] = [];

  selectedPlatforms: WritableSignal<JobPlatform[]> = signal([]);
  selectedAdditionalPlatforms: WritableSignal<JobPlatform[]> = signal([]);
  socialBoosterSelected: Signal<boolean> = computed(
    () =>
      this.selectedPlatforms().filter((platform) =>
        this.catchTalentSocialBooster(platform),
      ).length > 0,
  );

  showMoreStates: { [key: number]: boolean } = {};

  private readonly clipboard = inject(Clipboard);
  private readonly jobPlatformsService = inject(JobPlatformsService);
  private readonly toastsService = inject(ToastsService);
  public readonly fontawesomeService = inject(FontawesomeService);
  public readonly screenService = inject(ScreenService);

  ngOnInit(): void {
    this.jobPlatformsService.loadDefaultPlatforms();
    this.jobPlatformsService.loadRecommendedPlatforms();
    this.initialized.emit(true);
    this.initialized.complete();
  }

  totalPrice = computed(() => {
    let price: number = 0;

    const selectedPlatforms = this.selectedPlatforms();
    const selectedAdditionalPlatforms = this.selectedAdditionalPlatforms();

    selectedPlatforms.forEach((platform) => {
      if (this.isSourcedJobsPlatform(platform.id)) {
        return;
      }
      price += this.resolvePlatformPrice(platform);
    });

    selectedAdditionalPlatforms.forEach((platform) => {
      price += this.resolvePlatformPrice(platform);
    });

    return {
      price,
      vat: price * this.vat,
      totalPrice: price + price * this.vat,
    };
  });

  allSelectedPlatforms = computed(() => {
    const byKey = new Map<string, JobPlatform>();
    for (const p of [
      ...(this.selectedPlatforms() ?? []),
      ...(this.selectedAdditionalPlatforms() ?? []),
    ]) {
      byKey.set(`${p.provider}-${p.id}`, {
        ...p,
        price: this.isSourcedJobsPlatform(p.id)
          ? p.price
          : this.resolvePlatformPrice(p),
      });
    }
    return [...byKey.values()];
  });

  /**
   * Selected platforms that are billed in EUR (excludes LinkedIn Sourcing credits).
   * @returns {JobPlatform[]}
   */
  readonly payablePlatforms = computed(() =>
    this.allSelectedPlatforms().filter(
      (platform) => !this.isSourcedJobsPlatform(platform.id),
    ),
  );

  /**
   * Summary rows including LinkedIn Sourcing with credit amounts (not EUR).
   * @returns {JobPlatform[]}
   */
  readonly summaryPlatforms = computed(() => this.allSelectedPlatforms());

  /**
   * LinkedIn Sourcing can be added afterwards on Basis jobs when not already booked.
   * @returns {boolean}
   */
  readonly showSourcingCampaignSection = computed(() => {
    if (this.slotType() !== SlotType.BASIC) {
      return false;
    }
    return !this.bookedPlatformIds().some((bookedId) =>
      this.isSourcedJobsPlatform(bookedId),
    );
  });

  readonly selectedSourcingPlatform = computed(
    () =>
      this.selectedPlatforms().find((platform) =>
        this.isSourcedJobsPlatform(platform.id),
      ) ?? null,
  );

  catchTalentActiveSourcing = (platform: JobPlatform) =>
    String(platform.id) === '3' && platform.provider === 'catch';

  catchTalentSocialBooster = (platform: JobPlatform) =>
    String(platform.id) === '4' && platform.provider === 'catch';

  /**
   * Returns whether a platform id belongs to Sourced Jobs / LinkedIn Sourcing.
   * @param {string | number} platformId Platform id
   * @returns {boolean}
   */
  isSourcedJobsPlatform(platformId: string | number): boolean {
    return (
      String(platformId) === CatchJobPlatform.SOURCED_JOBS_LEADS ||
      String(platformId) === CatchJobPlatform.SOURCED_JOBS_AUTOPILOT
    );
  }

  /**
   * Returns whether the platform is the autopilot Sourced Jobs package.
   * @param {string | number} platformId Platform id
   * @returns {boolean}
   */
  isSourcedJobsAutopilot(platformId: string | number): boolean {
    return String(platformId) === CatchJobPlatform.SOURCED_JOBS_AUTOPILOT;
  }

  /**
   * Opens the LinkedIn Sourcing booking modal.
   * @returns {void}
   */
  openSourcingCampaignModal(): void {
    this.sourcingCampaignBookingModal?.open();
  }

  /**
   * Applies the selected LinkedIn Sourcing mode to the campaign selection.
   * @param {SourcedJobsCampaignMode} mode Selected campaign mode
   * @returns {void}
   */
  onSourcingCampaignConfirmed(mode: SourcedJobsCampaignMode): void {
    const platform = this.buildSourcingJobPlatform(mode);
    this.selectedPlatforms.update((platforms) => [
      ...platforms.filter((entry) => !this.isSourcedJobsPlatform(entry.id)),
      platform,
    ]);
  }

  /**
   * Removes LinkedIn Sourcing from the current selection.
   * @returns {void}
   */
  clearSourcingCampaignSelection(): void {
    this.selectedPlatforms.update((platforms) =>
      platforms.filter((platform) => !this.isSourcedJobsPlatform(platform.id)),
    );
  }

  /**
   * Builds a synthetic platform entry for a LinkedIn Sourcing campaign mode.
   * @param {SourcedJobsCampaignMode} mode Campaign mode
   * @returns {JobPlatform}
   */
  private buildSourcingJobPlatform(mode: SourcedJobsCampaignMode): JobPlatform {
    const isAutopilot = mode === SourcedJobsCampaignMode.AUTOPILOT;
    return {
      id: String(
        isAutopilot
          ? CatchJobPlatform.SOURCED_JOBS_AUTOPILOT
          : CatchJobPlatform.SOURCED_JOBS_LEADS,
      ),
      logoURL: '',
      title: isAutopilot
        ? 'Active Sourcing: Autopilot'
        : 'Active Sourcing: Longlist',
      duration: 0,
      price: isAutopilot
        ? CREDIT_COST_SOURCING_AUTOPILOT
        : CREDIT_COST_SOURCING_LEADS,
      default: false,
      active: true,
      description: '',
      selected: true,
      totalDeliveryTimePerDays: 0,
      provider: MultipostingProvider.CATCH,
    };
  }

  /**
   * Whether a Catch catalog platform is already booked on the selected job.
   * @param {JobPlatform} platform Catalog platform
   * @returns {boolean}
   */
  isBookedCatchPlatform = (platform: JobPlatform): boolean =>
    platform.provider === MultipostingProvider.CATCH &&
    this.bookedPlatformIds().some(
      (bookedId) => String(bookedId) === String(platform.id),
    );

  /**
   * Resolves Social Booster price by slot type from catalog defaults.
   * @param {JobPlatform} platform - Catalog or selected platform
   * @param {SlotType} [slotType] - Slot type override; defaults to current input
   * @returns {number} Price in EUR
   */
  resolvePlatformPrice(
    platform: JobPlatform,
    slotType: SlotType = this.slotType(),
  ): number {
    if (!this.catchTalentSocialBooster(platform)) {
      return platform.price;
    }
    const catalogPlatform =
      this.jobPlatformsService
        .defaultPlatforms()
        .find((catalogEntry) => this.catchTalentSocialBooster(catalogEntry)) ??
      platform;
    if (slotType === SlotType.ACTIVE_SOURCING) {
      return (
        catalogPlatform.agentAddOnPrice ??
        DEFAULT_SOCIAL_BOOSTER_AGENT_ADDON_PRICE
      );
    }
    return catalogPlatform.price ?? DEFAULT_SOCIAL_BOOSTER_PRICE;
  }

  displayedJobPlatforms = computed((): JobPlatform[] => {
    const uniquePlatforms = new Map<string, JobPlatform>();

    this.jobPlatformsService
      .defaultPlatforms()
      .forEach((p) => uniquePlatforms.set(`${p.provider}-${p.id}`, p));
    this.jobPlatformsService
      .recommendedPlatforms()
      .forEach((p) => uniquePlatforms.set(`${p.provider}-${p.id}`, p));
    this.selectedPlatforms().forEach((p) =>
      uniquePlatforms.set(`${p.provider}-${p.id}`, p),
    );

    return [...Array.from(uniquePlatforms.values())].filter(
      (jm) =>
        !this.catchTalentActiveSourcing(jm) &&
        !this.catchTalentSocialBooster(jm) &&
        jm.price !== 0,
    );
  });

  displayedFreeJobPlatforms = computed((): JobPlatform[] => {
    const slotType = this.slotType();
    return this.jobPlatformsService
      .defaultPlatforms()
      .filter(
        (platform) =>
          !this.catchTalentActiveSourcing(platform) &&
          (platform.price === 0 || this.catchTalentSocialBooster(platform)) &&
          !this.isBookedCatchPlatform(platform),
      )
      .map((platform) => ({
        ...platform,
        price: this.resolvePlatformPrice(platform, slotType),
        selected: this.selectedPlatforms().some(
          (selected) =>
            String(selected.id) === String(platform.id) &&
            selected.provider === platform.provider,
        ),
      }));
  });

  /**
   * Toggles a platform in or out of the current selection.
   * Uses membership in {@link selectedPlatforms} (not the row's `selected` flag).
   * @param {JobPlatform} jobPlatform - Platform row that was clicked
   * @returns {void}
   */
  onPlatformSelectionChange(jobPlatform: JobPlatform): void {
    const index = this.selectedPlatforms().findIndex(
      (p) =>
        String(p.id) === String(jobPlatform.id) &&
        p.provider === jobPlatform.provider,
    );
    if (index === -1) {
      this.selectedPlatforms.update((value) => [
        ...value,
        { ...jobPlatform, selected: true },
      ]);
    } else {
      this.selectedPlatforms.update((value) =>
        value.filter(
          (p) =>
            String(p.id) !== String(jobPlatform.id) ||
            p.provider !== jobPlatform.provider,
        ),
      );
    }
  }

  getAllSelectedJobPlatformIDs(): PlatformIdAndProvider[] {
    return this.selectedPlatforms().map((jobPlatform) => ({
      id: jobPlatform.id,
      provider: jobPlatform.provider,
    }));
  }

  reset() {
    this.jobPlatformFilter.reset();
    this.subscription.reset(
      this.poSubscriptions?.length === 1
        ? this.poSubscriptions[0]?.subscriptionId
        : null,
    );
    this.alreadyBooked = false;
    this.selectedPlatforms.set([]);
    this.selectedAdditionalPlatforms.set([]);
    this.jobPlatformsBrowser?.selectedPlatforms.set([]);
    this.ngOnInit();
  }

  copyPostingLink(ref: HTMLElement) {
    this.clipboard.copy(ref.innerHTML + this.jobPostingLink.value);
    this.toastsService.info(
      'company-tool.views.jobs.posting.sections.publication.toasts.copy-link',
    );
  }

  formatJobPostingLink(): void {
    this.jobPostingLink.setValue(
      (this.jobPostingLink.value ?? '').replace(/[^A-Za-z0-9-]/, ''),
    );
    if (this.jobPostingLink.value === '') this.jobPostingLink.setValue(null);
    this.jobPostingLink.updateValueAndValidity();
  }

  trackPlatformsBy(index: number, element: JobPlatform) {
    return `${element.provider}-${element.id}`;
  }

  openJobCrossPostingModal(modal: any, jobPlatform: JobPlatform) {
    this.jobCrossPostings = jobPlatform.crossPostings ?? [];
    modal.open();
  }

  toggleShowMore(id: number) {
    this.showMoreStates[id] = !this.showMoreStates[id];
  }

  onAdditionalPlatformSelectionChange(jobPlatforms: JobPlatform[]): void {
    this.selectedAdditionalPlatforms.set(jobPlatforms);
  }
}
